import React from 'react';
import { Box } from '@wix/design-system';
import classNames from 'classnames';
import {
  AiAnalyzingBorder,
  useAiBorderMode,
  useCurrentModule,
  useEffectiveRecommendation,
  useAiRecommendationEnabled,
  ECLIPSE_PRIMARY_COLOR,
  ECLIPSE_BORDER_COLOR,
  useSelectButton,
} from '@wix/package-picker-shared-eclipse';
import { ProductCardList } from '@wix/premium-purchase-platform/@components';
import { CardContent } from './CardContent';
import { CardHatSlot } from './CardHatSlot';
import { AiPlanRecommendationCard } from '../../AiPlanRecommendationCard';
import { AiPlanRecommendationBannerDummy } from '../../AiPlanRecommendationCard/AiPlanRecommendationBannerDummy';
import { useAiBannerRowPlacement } from './useAiBannerRowPlacement';
import s from './ProductCard.scss';

interface BaseProductCardProps {
  productId: string;
  cardIndex?: number;
  cardCount?: number;
}

export const BaseProductCard = ({
  productId,
  cardIndex = 0,
  cardCount,
}: BaseProductCardProps) => {
  const module = useCurrentModule();
  const { data: product } =
    module.SDK.eclipseOfferingApiClient.hooks.useFetchProduct({ productId });
  const { name } = product ?? {};
  const { SelectButton } = useSelectButton({ productId });
  const { isEnabled: isAiRecommendationEnabled } = useAiRecommendationEnabled();
  const { mode: borderMode, hidesCardBorder } = useAiBorderMode({ productId });
  const { isRecommended, isAiRecommended } = useEffectiveRecommendation(
    productId,
    product?.isRecommended ?? false,
  );

  const rowBannerPlacement = useAiBannerRowPlacement();
  const bannerPlacement = isAiRecommended ? rowBannerPlacement : 'none';
  const dummyPlacement = isAiRecommended ? 'none' : rowBannerPlacement;

  React.useEffect(() => {
    module.SDK.aiRecommendationStore.markPlansRendered();
  }, [module]);

  const borderColor = hidesCardBorder
    ? 'transparent'
    : isRecommended
    ? ECLIPSE_PRIMARY_COLOR
    : ECLIPSE_BORDER_COLOR;
  const borderWidth =
    isAiRecommendationEnabled || !isRecommended ? '1px' : '2px';
  const borderValue = `${borderWidth} solid ${borderColor}`;

  return (
    <Box direction="vertical" width="100%" maxWidth="370px" flex={1}>
      {dummyPlacement === 'top' && (
        <AiPlanRecommendationBannerDummy placement="top" />
      )}
      <ProductCardList.Default.Card.Root
        width="100%"
        paddingLeft="20px"
        paddingRight="20px"
        paddingBottom="40px"
        maxWidth="370px"
        minHeight="450px"
        borderRadius="16px"
        borderTop={borderValue}
        borderBottom={borderValue}
        borderLeft={borderValue}
        borderRight={borderValue}
        className={
          isAiRecommendationEnabled
            ? classNames(s.cardRoot, {
                [s.cardRecommended]: isRecommended,
              })
            : undefined
        }
      >
        {isAiRecommendationEnabled && (
          <AiAnalyzingBorder
            mode={borderMode}
            cardIndex={cardIndex}
            cardCount={cardCount}
          />
        )}
        {bannerPlacement === 'top' && (
          <AiPlanRecommendationCard bannerPlacement="top" />
        )}
        <Box
          width="100%"
          marginTop="SP3"
          marginBottom="SP4"
          className={s.productHatWrapper}
        >
          <CardHatSlot
            productId={productId}
            bannerPlacement={bannerPlacement}
            dummyPlacement={dummyPlacement}
          />
        </Box>
        <Box
          width="100%"
          direction="vertical"
          boxSizing="border-box"
          flexGrow={1}
        >
          <CardContent
            name={name}
            isRecommended={isRecommended}
            SelectButton={SelectButton}
            productId={productId}
          />
        </Box>
      </ProductCardList.Default.Card.Root>
    </Box>
  );
};
